import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AZURE } from '../services/api';
import { VUKZ } from '../theme/vukzTheme';
import SEOHead from '../components/seo/SEOHead';
import { ArticleSchema } from '../components/seo/StructuredData';
import {
  Clock, Eye, ArrowLeft, Share2, BookOpen,
  BarChart3, Beaker, ChevronRight,
} from 'lucide-react';
import {
  StudyChartBlock,
  StudyMethodologyBox,
  StudyConclusionBox,
  StudyInsightCallout,
} from '../components/studies';

/**
 * PublicInsightArticlePage — Detalhe de um estudo / artigo
 * Route: /estudos/:slug
 * Design: VUKZ header (navbar + logo), corpo minimal de leitura.
 */

const SPORT_LABELS = {
  swimming: { emoji: '🏊', label: 'Natação' },
  football: { emoji: '⚽', label: 'Futebol' },
  general:  { emoji: '📊', label: 'Geral' },
};

/* ─── JSON parse helper ─── */
const parseJson = (val) => {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return null; }
};

/* ───────────────────────────────────────────────────────────── */
const PublicInsightArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await AZURE.get(`/editorial/public/articles/${slug}`);
        if (data.success) setArticle(data.data);
        else setError('Artigo não encontrado');
      } catch (err) {
        console.error('Error fetching article:', err);
        setError(
          err.response?.status === 404
            ? 'Artigo não encontrado'
            : 'Erro ao carregar artigo',
        );
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  /* Share */
  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: article?.title || 'VUKZ Estudo',
          text: article?.excerpt || '',
          url,
        });
      } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="max-w-3xl mx-auto px-6 pt-16 animate-pulse space-y-5">
          <div className="h-3 bg-gray-100 rounded w-32" />
          <div className="h-8 bg-gray-100 rounded w-3/4" />
          <div className="h-4 bg-gray-100 rounded w-1/2" />
          <div className="h-64 bg-gray-50 rounded-2xl mt-8" />
          <div className="space-y-3 mt-6">
            <div className="h-3 bg-gray-100 rounded" />
            <div className="h-3 bg-gray-100 rounded w-5/6" />
            <div className="h-3 bg-gray-100 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  /* ── Error state ── */
  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="flex items-center justify-center pt-32">
          <div className="text-center">
            <p className="text-gray-400 text-lg mb-4">{error}</p>
            <Link
              to="/estudos"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4" /> Voltar para Estudos
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const sport = SPORT_LABELS[article.sport] || SPORT_LABELS.general;
  const isStudy = article.category === 'study';

  const chartBlocks    = parseJson(article.chart_blocks_json) || [];
  const keyFindings    = parseJson(article.key_findings_json) || [];
  const takeaways      = parseJson(article.practical_takeaways_json) || [];
  const limitations    = parseJson(article.limitations_json) || [];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={article.title}
        description={article.excerpt || article.title}
        path={`/estudos/${article.slug}`}
        type="article"
      />
      <ArticleSchema article={article} />
      {/* ── VUKZ Navbar ── */}
      <NavBar onShare={handleShare} />

      {/* ── Article wrapper ── */}
      <article className="max-w-3xl mx-auto px-6 pt-12 pb-20">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-gray-300 mb-8">
          <Link to="/" className="hover:text-gray-500 transition-colors">Início</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/estudos" className="hover:text-gray-500 transition-colors">Estudos</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-gray-500 truncate max-w-[200px]">
            {article.title}
          </span>
        </nav>

        {/* Cover image */}
        {article.cover_image_url && (
          <div className="w-full h-64 rounded-2xl overflow-hidden mb-8">
            <img
              src={article.cover_image_url}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Tags */}
        <div className="flex items-center gap-3 mb-5">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
            {sport.emoji} {sport.label}
          </span>
          {isStudy && (
            <span className="text-[11px] font-semibold uppercase tracking-widest text-indigo-500">
              · Estudo Interativo
            </span>
          )}
          {article.is_featured && (
            <span className="text-[11px] font-bold uppercase tracking-widest text-amber-500">
              · Destaque
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight tracking-tight">
          {article.title}
        </h1>

        {/* Excerpt */}
        {article.excerpt && (
          <p className="text-base text-gray-400 mt-4 leading-relaxed max-w-2xl">
            {article.excerpt}
          </p>
        )}

        {/* Author + date */}
        <div className="flex items-center gap-4 mt-8 pb-8 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {article.author_avatar_url ? (
              <img
                src={article.author_avatar_url}
                alt={article.author_name}
                className="w-9 h-9 rounded-full"
              />
            ) : (
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ backgroundColor: VUKZ.primary }}
              >
                <BookOpen className="w-4 h-4 text-white" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {article.author_name || 'VUKZ Editorial'}
              </p>
              <div className="flex items-center gap-3 text-[11px] text-gray-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {article.published_at
                    ? new Date(article.published_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })
                    : '—'}
                </span>
                {article.view_count > 0 && (
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {article.view_count}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════
            STUDY-SPECIFIC LAYOUT
            ═══════════════════════════════════════════════════════ */}
        {isStudy && (
          <div className="mt-10 space-y-10">

            {/* Executive Summary */}
            {article.executive_summary && (
              <section>
                <SectionLabel icon={<BarChart3 className="w-4 h-4" />} text="Resumo executivo" />
                <p className="text-base text-gray-700 leading-relaxed mt-3">
                  {article.executive_summary}
                </p>
              </section>
            )}

            {/* Key Findings */}
            {keyFindings.length > 0 && (
              <StudyInsightCallout variant="insight" title="Principais descobertas">
                <ul className="space-y-2 mt-2">
                  {keyFindings.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-amber-500 font-bold mt-0.5">→</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </StudyInsightCallout>
            )}

            {/* Chart blocks (interpretive — data loaded separately) */}
            {chartBlocks.length > 0 && (
              <section>
                <SectionLabel
                  icon={<Beaker className="w-4 h-4" />}
                  text={`Análise · ${chartBlocks.length} gráfico${chartBlocks.length > 1 ? 's' : ''}`}
                />
                <div className="mt-4 space-y-6">
                  {chartBlocks
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((block, i) => (
                      <StudyChartBlock
                        key={i}
                        chartType={block.chart_type || 'line'}
                        title={block.title}
                        question={block.question_answered}
                        data={[]}
                        xField={block.x_field || 'year'}
                        yField={block.y_field || 'avg_time_seconds'}
                        reversedY={block.reversed_y || false}
                        whatItShows={block.what_it_shows}
                        whatStandsOut={block.what_stands_out}
                        practicalMeaning={block.practical_meaning}
                      />
                    ))}
                </div>
              </section>
            )}

            {/* Conclusions + Takeaways */}
            <StudyConclusionBox
              summary={article.executive_summary}
              takeaways={takeaways}
            />

            {/* Methodology + Limitations */}
            <StudyMethodologyBox
              methodology={article.methodology_notes}
              dataSource={article.data_endpoint}
              period="2019–2025"
              limitations={limitations}
              defaultOpen={false}
            />
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════
            STANDARD BODY (for non-study or additional content)
            ═══════════════════════════════════════════════════════ */}
        {article.body_html ? (
          <div
            className={`prose prose-gray prose-lg max-w-none
              prose-headings:font-bold prose-headings:text-gray-900
              prose-p:text-gray-600 prose-p:leading-relaxed
              prose-a:text-gray-900 prose-a:underline prose-a:underline-offset-2
              prose-strong:text-gray-900
              ${isStudy ? 'mt-12 pt-10 border-t border-gray-100' : 'mt-8'}`}
            dangerouslySetInnerHTML={{ __html: article.body_html }}
          />
        ) : article.body_markdown && !isStudy ? (
          <div className="prose prose-gray prose-lg max-w-none whitespace-pre-wrap text-gray-600 mt-8">
            {article.body_markdown}
          </div>
        ) : null}

        {/* Confidence disclaimer */}
        {article.confidence_score != null && article.confidence_score < 0.8 && (
          <div className="mt-10 p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <p className="text-sm text-gray-500">
              ⚠️ <strong>Nota:</strong> Conteúdo gerado com dados em consolidação
              (confiança: {(article.confidence_score * 100).toFixed(0)}%).
            </p>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-16 pt-10 border-t border-gray-100 text-center">
          <p className="text-sm text-gray-400 mb-4">
            Explore mais análises baseadas em dados reais.
          </p>
          <Link
            to="/estudos"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: VUKZ.primary }}
          >
            Ver todos os estudos
          </Link>
        </div>
      </article>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-3xl mx-auto px-6 flex items-center justify-between">
          <img src="/vukz-logo.jpeg" alt="VUKZ" className="h-5 w-auto opacity-30" />
          <p className="text-[11px] text-gray-300">
            Dados reais · Análise aprofundada
          </p>
        </div>
      </footer>
    </div>
  );
};

/* ─── Shared sub-components ─── */

/** VUKZ Navbar — consistent with homepage */
const NavBar = ({ onShare }) => (
  <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
    <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <img src="/vukz-logo.jpeg" alt="VUKZ" className="h-7 w-auto" />
      </Link>

      <div className="flex items-center gap-4">
        <Link
          to="/estudos"
          className="text-sm font-medium text-gray-400 hover:text-gray-900 transition-colors flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Estudos
        </Link>
        {onShare && (
          <button
            onClick={onShare}
            className="text-sm text-gray-400 hover:text-gray-900 transition-colors flex items-center gap-1"
          >
            <Share2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  </nav>
);

/** Section label — small caps accent */
const SectionLabel = ({ icon, text }) => (
  <div className="flex items-center gap-2">
    <span className="text-gray-400">{icon}</span>
    <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-400">
      {text}
    </span>
  </div>
);

export default PublicInsightArticlePage;
